<!--
  ClientApprovalAccordion.vue -- the design-level approval region.

  Card: Decoration color + family model (https://trello.com/c/2hcgDM7J)
  Handoff: planning/design-handoffs/handoff_2026-08-23/
           2026-08-23-decoration-family-model/
  ADR 0067, Locked Decisions 11, 12, 13, 16.

  The second grain, pivoted from the first
  ========================================
  Approval is displayed at exactly two grains, both AT or BELOW the
  size (ADR 0067):

    1. on a size row -- one pill per client   (DesignSizeRow.vue)
    2. here          -- client-first, sizes nested

  Same data, two questions. "Is this size cleared?" is a row question;
  "what does the Booster Club still owe me?" is a client question.

  This component gets its rows from `clientApprovalSummary`, which does
  not read `size.approvals` itself -- it calls `approvalsForSize`, the
  same selector the size rows use, and pivots the result. So the two
  grains are one derivation viewed two ways rather than two
  derivations that happen to agree today. The handoff prototype does it
  the other way (`dm-parts.jsx:186` open-codes `v.appr[client]` beside
  `dm-parts.jsx:31`'s `apprList`), which is the drift
  `docs/design-philosophy.md` principle 4 warns about.

  What this is NOT
  ================
  Not a clients x sizes matrix (Locked Decision 12). At the real
  ceiling of 4 clients x 8 sizes a grid is 32 coloured cells with no
  room for when it was sent, which version it was given against, or
  what the client actually said -- it looks like an answer and is a
  lookup table. Collapsed, this component shows no size cells at all.

  A design with no client is NOT an empty state (Locked Decision 16):
  the region explains that approvals are requested per size once a
  client exists. No placeholder rows, no warning colour.

  Deliberately absent: the change-request TEXT (Locked Decision 14) and
  the per-client actions (Locked Decision 21) both live in the size
  flyover, which is a separate unit. Quoting a client verbatim in a
  collapsed summary row would be the flyover leaking upward.
-->
<template>
    <div class="caa" data-testid="client-approval-accordion">
        <!-- Locked Decision 16 -- an explanation, not an empty state. -->
        <div
            v-if="!clients.length"
            class="caa__noclient"
            data-testid="approval-no-client-note"
        >
            No client on this design yet. Approvals are requested per size,
            so the list appears once a size has been sent to a client.
        </div>

        <div v-else class="caa__list">
            <div
                v-for="client in clients"
                :key="client.clientId"
                class="caa__client"
                :data-testid="`approval-client-row-${client.clientId}`"
            >
                <button
                    type="button"
                    class="caa__head"
                    :class="{ 'caa__head--open': isOpen(client.clientId) }"
                    :aria-expanded="isOpen(client.clientId) ? 'true' : 'false'"
                    :data-testid="`approval-client-toggle-${client.clientId}`"
                    @click="toggle(client.clientId)"
                >
                    <span
                        class="caa__caret"
                        :class="{
                            'caa__caret--open': isOpen(client.clientId),
                        }"
                        aria-hidden="true"
                    >&rsaquo;</span>

                    <span class="caa__headtext">
                        <span class="caa__name">{{ client.clientName }}</span>
                        <span
                            class="caa__count"
                            :data-testid="
                                `approval-client-count-${client.clientId}`
                            "
                        >{{ client.approvedCount }} of
                            {{ client.totalCount }}
                            {{ client.totalCount === 1 ? "size" : "sizes" }}
                            approved</span>
                    </span>

                    <!--
                      The worst OUTSTANDING state with its count. A
                      fully-approved client has nothing outstanding, so
                      it gets the quiet done-marker instead rather than
                      a chip reading "0 of something".
                    -->
                    <span
                        v-if="client.worstState"
                        class="caa__worst"
                        :data-testid="
                            `approval-client-worst-${client.clientId}`
                        "
                    >
                        <ApprovalStatePill :state="client.worstState" />
                        <span class="caa__worstcount">
                            &times;{{ client.worstCount }}
                        </span>
                    </span>
                    <span
                        v-else
                        class="caa__alldone"
                        :data-testid="
                            `approval-client-alldone-${client.clientId}`
                        "
                    >All sizes approved</span>
                </button>

                <!-- Sizes nested, so the size grain is never implied away. -->
                <table
                    v-if="isOpen(client.clientId)"
                    class="caa__table"
                    :data-testid="`approval-client-sizes-${client.clientId}`"
                >
                    <thead>
                        <tr>
                            <th scope="col">Size</th>
                            <th scope="col">Approval</th>
                            <th scope="col">Given against</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="size in client.sizes"
                            :key="size.sizeId"
                            :data-testid="
                                `approval-size-cell-${client.clientId}-${size.sizeId}`
                            "
                        >
                            <td
                                class="caa__dims"
                                :data-testid="
                                    `approval-size-dims-${client.clientId}-${size.sizeId}`
                                "
                            >
                                {{ dimensionsOf(size) }}
                                <span
                                    v-if="size.substrateTarget"
                                    class="caa__sub"
                                >{{ size.substrateTarget }}</span>
                            </td>
                            <td>
                                <ApprovalStatePill :state="size.state" />
                            </td>
                            <!--
                              Locked Decision 15 -- an approval always
                              states the version it was given against.
                              A dash when nothing has been sent, which
                              is honest; defaulting to the current
                              version would assert an approval of a
                              version the client never saw.
                            -->
                            <td class="caa__version">
                                <template v-if="size.versionNumber != null">
                                    v{{ size.versionNumber }}
                                </template>
                                <template v-else>&mdash;</template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import { clientApprovalSummary } from "@/utils/decorationFamilySelectors";

import ApprovalStatePill from "./ApprovalStatePill.vue";

function trimNumber(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return String(Number(n.toFixed(2)));
}

export default {
    name: "ClientApprovalAccordion",
    components: { ApprovalStatePill },
    props: {
        /** The whole family payload. */
        family: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            // Client id -> expanded. Seeded in `created` so a
            // single-client design opens as a sentence rather than as
            // something to click.
            expanded: {},
        };
    },
    computed: {
        clients() {
            return clientApprovalSummary(this.family);
        },
    },
    created() {
        if (this.clients.length === 1) {
            this.expanded = { [this.clients[0].clientId]: true };
        }
    },
    methods: {
        isOpen(clientId) {
            return !!this.expanded[clientId];
        },
        toggle(clientId) {
            this.expanded = {
                ...this.expanded,
                [clientId]: !this.expanded[clientId],
            };
        },
        dimensionsOf(size) {
            const w = trimNumber(size.widthIn);
            const h = trimNumber(size.heightIn);
            if (w == null || h == null) return "Dimensions unknown";
            return `${w}" x ${h}"`;
        },
    },
};
</script>

<style scoped>
.caa {
    margin-top: 4px;
}
.caa__noclient {
    padding: 22px 18px;
    background: var(--tf-surface-alt);
    border: 1px dashed var(--tf-border-strong);
    border-radius: var(--tf-radius-md);
    font-size: 13px;
    color: var(--tf-fg-3);
    line-height: 1.6;
}
.caa__list {
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-md);
    overflow: hidden;
    background: var(--tf-surface);
}
.caa__client + .caa__client {
    border-top: 1px solid var(--tf-border);
}
.caa__head {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 13px 16px;
    background: var(--tf-surface);
    border: 0;
    text-align: left;
    cursor: pointer;
    font: inherit;
}
.caa__head--open {
    background: var(--tf-surface-alt);
}
.caa__caret {
    font-size: 18px;
    color: var(--tf-fg-4);
    transition: transform 150ms;
    display: inline-block;
}
.caa__caret--open {
    transform: rotate(90deg);
}
.caa__headtext {
    flex: 1;
    min-width: 0;
}
.caa__name {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: var(--tf-fg-1);
}
.caa__count {
    display: block;
    font-size: 12px;
    color: var(--tf-fg-4);
    margin-top: 2px;
}
.caa__worst {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
}
.caa__worstcount {
    font-size: 12px;
    color: var(--tf-fg-4);
}
.caa__alldone {
    font-size: 12px;
    color: var(--tf-fg-4);
    flex-shrink: 0;
}
.caa__table {
    width: 100%;
    border-collapse: collapse;
    background: var(--tf-surface);
    font-size: 13px;
}
.caa__table th {
    text-align: left;
    padding: 8px 16px;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: var(--tf-fg-4);
    background: var(--tf-surface-alt);
    border-top: 1px solid var(--tf-border);
    border-bottom: 1px solid var(--tf-border);
}
.caa__table td {
    padding: 10px 16px;
    border-bottom: 1px solid var(--tf-border);
}
.caa__dims {
    font-variant-numeric: tabular-nums;
    font-size: 12.5px;
    color: var(--tf-fg-1);
    white-space: nowrap;
}
.caa__sub {
    margin-left: 6px;
    font-size: 10.5px;
    color: var(--tf-fg-4);
    text-transform: uppercase;
    letter-spacing: 0.6px;
}
.caa__version {
    font-variant-numeric: tabular-nums;
    font-size: 12px;
    color: var(--tf-fg-4);
    white-space: nowrap;
}
</style>
